import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import "./search.m.css"; // J-number sort update
import ImageType3 from "../molecules/image-type-3";
import { Link } from "@quintype/components";

const getQueryFromUrl = () => {
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("q") || "";
  }
  return "";
};

const normalizeText = (str) =>
  (str || "")
    .toLowerCase()
    .replace(/[äå]/g, "a")
    .replace(/[ö]/g, "o")
    .trim();

const filterStories = (stories, query, tab = "all") => {
  if (!query) return [];
  const terms = query.split(/[\s-]+/).map(normalizeText).filter(Boolean);
  if (terms.length === 0) return stories;

  return stories
    .filter((story) => {
      const headline = normalizeText(story.headline);
      const subheadline = normalizeText(story.subheadline);
      const slug = normalizeText(story.slug);
      const caption = normalizeText(story["hero-image-caption"]);
      
      const sectionNames = (story.sections || []).map((s) => normalizeText(s.name)).join(" ");
      const sectionSlugs = (story.sections || []).map((s) => normalizeText(s.slug)).join(" ");
      
      const tags = (story.tags || []).map((t) => normalizeText(t.name)).join(" ");
      const alternativeHeadline = normalizeText(story.alternative?.home?.default?.headline);

      if (tab === "series") {
        return terms.every((term) => sectionNames.includes(term) || sectionSlugs.includes(term));
      }
      if (tab === "guest") {
        return terms.every((term) => headline.includes(term) || caption.includes(term));
      }
      if (tab === "topic") {
        return terms.every((term) => subheadline.includes(term));
      }

      // Default: "all"
      return terms.every(
        (term) =>
          headline.includes(term) ||
          subheadline.includes(term) ||
          slug.includes(term) ||
          caption.includes(term) ||
          sectionNames.includes(term) ||
          sectionSlugs.includes(term) ||
          tags.includes(term) ||
          alternativeHeadline.includes(term)
      );
    });
};

const parseEpisodeKey = (headline) => {
  const m = String(headline).match(/(\d+)\/(\d{4})/);
  if (!m) return null;
  const num = parseInt(m[1], 10);
  const year = parseInt(m[2], 10);
  if (year < 2000 || year > 2100) return null;
  return { year, num };
};

const parseJNumber = (headline) => {
  const m = String(headline).match(/\bJ\s*(\d+)\b/i);
  return m ? parseInt(m[1], 10) : null;
};

const compareKeys = (a, b) => {
  if (a && b) {
    if (b.year !== a.year) return b.year - a.year;
    return b.num - a.num;
  }
  return a ? -1 : b ? 1 : 0;
};

const compareEpisodes = (a, b) => {
  const keyA = parseEpisodeKey(a.headline);
  const keyB = parseEpisodeKey(b.headline);
  
  if (keyA && keyB) {
    if (keyB.year !== keyA.year) return keyB.year - keyA.year;
    return keyB.num - keyA.num;
  }
  if (keyA) return -1;
  if (keyB) return 1;

  const jA = parseJNumber(a.headline);
  const jB = parseJNumber(b.headline);
  if (jA !== null && jB !== null) return jA - jB;
  if (jA !== null) return -1;
  if (jB !== null) return 1;

  const aTime = new Date(a["first-published-at"] || a["published-at"] || 0).getTime();
  const bTime = new Date(b["first-published-at"] || b["published-at"] || 0).getTime();
  return bTime - aTime;
};

const groupStoriesBySeries = (stories) => {
  const seriesMap = {};
  
  stories.forEach((story) => {
    const section = story.sections?.[0];
    let seasonName = "";
    let seriesName = section ? section.name : "Muut";
    
    if (section && section.name && section.name.includes("/")) {
      const parts = section.name.split("/");
      seasonName = parts[0].trim();
      seriesName = parts.slice(1).join("/").trim();
    }
    
    const seriesKey = seriesName.toLowerCase();
    if (!seriesMap[seriesKey]) {
      seriesMap[seriesKey] = {
        id: section ? section.id : "none",
        name: seriesName,
        section: section || { id: "none", name: "Muut", slug: "muut", "section-url": "#" },
        storiesCount: 0,
        seasonsMap: {},
      };
    }
    
    const seasonKey = seasonName || "main";
    if (!seriesMap[seriesKey].seasonsMap[seasonKey]) {
      seriesMap[seriesKey].seasonsMap[seasonKey] = {
        name: seasonName,
        stories: [],
      };
    }
    
    seriesMap[seriesKey].seasonsMap[seasonKey].stories.push(story);
    seriesMap[seriesKey].storiesCount += 1;
  });
  
  return Object.values(seriesMap).map((series) => {
    const seasonsList = Object.values(series.seasonsMap).map((season) => {
      season.stories.sort(compareEpisodes);
      return season;
    });

    seasonsList.sort((a, b) => {
      if (!a.name) return 1;
      if (!b.name) return -1;
      return b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: "base" });
    });

    const allSeriesStories = seasonsList.flatMap((s) => s.stories);

    let bestKey = null;
    let fallbackMs = 0;
    allSeriesStories.forEach((story) => {
      const k = parseEpisodeKey(story.headline);
      if (k) {
        if (!bestKey || k.year > bestKey.year || (k.year === bestKey.year && k.num > bestKey.num)) {
          bestKey = k;
        }
      }
      const ms = new Date(story["first-published-at"] || story["published-at"] || 0).getTime();
      fallbackMs = Math.max(fallbackMs, ms);
    });

    return {
      id: series.id,
      name: series.name,
      section: series.section,
      storiesCount: series.storiesCount,
      seasons: seasonsList,
      hasMultipleSeasons: seasonsList.length > 1 || (seasonsList.length === 1 && !!seasonsList[0].name),
      bestKey,
      fallbackMs,
    };
  }).sort((a, b) => {
    const c = compareKeys(a.bestKey, b.bestKey);
    if (c !== 0) return c;
    return b.fallbackMs - a.fallbackMs;
  });
};

const SearchPage = (props) => {
  const limit = 4000; // Load all catalog stories
  const rawInitialStories = props.data?.stories || [];
  const initialQuery = props.data?.query || getQueryFromUrl() || "";

  const allSections = useSelector((state) => get(state, ["qt", "config", "sections"], []));

  const [allStories, setAllStories] = useState(rawInitialStories);
  const [orderedStories, setOrderedStories] = useState([]);
  const [queryValue, setQueryValue] = useState(initialQuery);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [viewMode, setViewMode] = useState("episodes"); // "episodes" | "series"
  const [visibleCount, setVisibleCount] = useState(25);
  const [episodesLimit, setEpisodesLimit] = useState({});
  const [seriesSummaries, setSeriesSummaries] = useState({});

  const fetchAllStories = async () => {
    const stories = await fetch(
      `/api/v1/advanced-search?limit=${limit}&fields=id,headline,subheadline,slug,sections,tags,hero-image-s3-key,hero-image-caption,access,first-published-at,published-at,alternative`
    );
    return await stories.json();
  };

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setLoading(true);
        const response = await fetchAllStories();
        const res = response?.items || [];
        setAllStories(res);
      } catch (error) {
        console.error("Failed to fetch search catalog:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, []);

  useEffect(() => {
    const filtered = filterStories(allStories, queryValue, "all");
    filtered.sort(compareEpisodes);
    setOrderedStories(filtered);
  }, [queryValue, allStories]);

  useEffect(() => {
    setVisibleCount(25);
    setEpisodesLimit({});
  }, [queryValue, viewMode]);

  // Group ALL filtered stories by series so order remains completely stable
  const allSeriesGroups = groupStoriesBySeries(orderedStories);

  const resolveCollectionSlug = (group) => {
    if (!group.section || group.section.id === "none") return "";
    const currentSec = allSections.find((s) => s.id === group.section.id);
    if (!currentSec) return group.section.slug || "";

    let seriesSec = currentSec;
    if (group.section.name && group.section.name.includes("/")) {
      seriesSec = allSections.find((s) => s.id === currentSec["parent-id"]) || currentSec;
    }

    const parentSec = seriesSec["parent-id"]
      ? allSections.find((s) => s.id === seriesSec["parent-id"])
      : null;

    if (parentSec && parentSec.slug && parentSec.slug !== "home") {
      return `${seriesSec.slug}-${parentSec.slug}`;
    }
    return seriesSec.slug || "";
  };

  useEffect(() => {
    if (allSeriesGroups.length === 0) return;

    const fetchSummaries = async () => {
      const summaries = { ...seriesSummaries };
      let updated = false;

      await Promise.all(
        allSeriesGroups.map(async (group) => {
          const slug = resolveCollectionSlug(group);

          if (slug && !summaries[slug]) {
            try {
              const res = await fetch(`/api/v1/collections/${slug}`);
              if (res.ok) {
                const data = await res.json();
                if (data?.summary) {
                  summaries[slug] = data.summary;
                  updated = true;
                }
              }
            } catch (err) {
              console.error(`Failed to fetch summary for collection: ${slug}`, err);
            }
          }
        })
      );

      if (updated) {
        setSeriesSummaries(summaries);
      }
    };

    fetchSummaries();
  }, [allSeriesGroups]);

  // Sliced matching stories for flat episode view
  const slicedStories = orderedStories.slice(0, visibleCount);

  // Calculate unique series count and total episodes count
  const totalEpisodesCount = orderedStories.length;
  const uniqueSeriesCount = allSeriesGroups.length;

  const getSeriesUrl = (groupSection) => {
    if (!groupSection || groupSection.id === "none") return "#";
    const currentSec = allSections.find((s) => s.id === groupSection.id);
    const parentId = currentSec ? currentSec["parent-id"] : null;
    if (parentId) {
      const parentSec = allSections.find((s) => s.id === parentId);
      if (parentSec) {
        return parentSec["section-url"] || `/${parentSec.slug}`;
      }
    }
    return groupSection["section-url"] || `/${groupSection.slug}`;
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const formattedQuery = queryValue
    ? decodeURIComponent(queryValue)
        .replace(/-/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "";

  return (
    <React.Fragment>
      <div styleName="search-results">
        <h2 styleName="search-results-title">Haku: "{formattedQuery}"</h2>
        {!loading && (
          <p styleName="search-results-sub">
            Löytyi {uniqueSeriesCount} {uniqueSeriesCount === 1 ? "sarja" : "sarjaa"} ja {totalEpisodesCount} {totalEpisodesCount === 1 ? "jakso" : "jaksoa"}.
          </p>
        )}
      </div>

      <div styleName="search-wrapper">
        {!loading && orderedStories.length > 0 && (
          <div styleName="view-toggle-container">
            <button
              styleName={viewMode === "episodes" ? "toggle-btn toggle-active" : "toggle-btn"}
              onClick={() => setViewMode("episodes")}
            >
              Jaksonäkymä ({totalEpisodesCount})
            </button>
            <button
              styleName={viewMode === "series" ? "toggle-btn toggle-active" : "toggle-btn"}
              onClick={() => setViewMode("series")}
            >
              Sarjanäkymä ({uniqueSeriesCount})
            </button>
          </div>
        )}

        {loading ? (
          <div styleName="loading-container">
            <div styleName="loading-spinner"></div>
            <p styleName="loading-text">Etsitään ja ladataan sisältöä...</p>
          </div>
        ) : orderedStories.length > 0 ? (
          viewMode === "series" ? (
            <div styleName="groups-list">
              {allSeriesGroups.map((group) => {
                const defaultExpanded = totalEpisodesCount <= 5;
                const isExpanded = expandedSections[group.id] !== undefined
                  ? expandedSections[group.id]
                  : defaultExpanded;
                return (
                  <div key={group.id} styleName="series-collapsible-card">
                    <div
                      styleName="series-card-header"
                      onClick={() => toggleSection(group.id)}
                    >
                      <div styleName="series-card-title-container">
                        <span styleName="series-card-title">{group.name}</span>
                        {(() => {
                          const slug = resolveCollectionSlug(group);
                          return seriesSummaries[slug] ? (
                            <div styleName="series-card-summary">{seriesSummaries[slug]}</div>
                          ) : null;
                        })()}
                        <span styleName="series-card-count">
                          {group.storiesCount} {group.storiesCount === 1 ? "jakso" : "jaksoa"}
                        </span>
                      </div>
                      <div styleName="series-card-actions">
                        {group.id !== "none" && (
                          <Link
                            href={getSeriesUrl(group.section)}
                            styleName="series-view-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Siirry sarjaan
                          </Link>
                        )}
                        <div styleName={isExpanded ? "arrow-icon arrow-expanded" : "arrow-icon"}>
                          <i className="ri-arrow-down-s-line"></i>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (() => {
                      const limit = episodesLimit[group.id] || 25;
                      let renderedCount = 0;

                      return (
                        <div styleName="series-card-episodes">
                          {group.seasons.map((season) => {
                            const remainingLimit = limit - renderedCount;
                            if (remainingLimit <= 0) return null;

                            const visibleStories = season.stories.slice(0, remainingLimit);
                            renderedCount += visibleStories.length;

                            return (
                              <div key={season.name || "main"} styleName="season-subgroup">
                                {group.hasMultipleSeasons && season.name && (
                                  <h4 styleName="season-sub-header">{season.name}</h4>
                                )}
                                <div styleName="season-episodes-list">
                                  {visibleStories.map((story) => (
                                    <div key={story.id} styleName="search-card-wrapper">
                                      <ImageType3
                                        story={story}
                                        showSection={false}
                                        showDateSection={false}
                                        isSearchPage={true}
                                        aspectRatio={[[16, 9], [16, 9]]}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                          {group.storiesCount > limit && (
                            <button
                              styleName="local-loadmore-btn"
                              onClick={() => setEpisodesLimit((prev) => ({ ...prev, [group.id]: limit + 20 }))}
                            >
                              Näytä lisää jaksoja ({group.storiesCount - limit} jäljellä)
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          ) : (
            <div styleName="episodes-list">
              {slicedStories.map((story) => (
                <div key={story.id} styleName="search-card-wrapper">
                  <ImageType3
                    story={story}
                    showSection={true}
                    showDateSection={true}
                    isSearchPage={true}
                    aspectRatio={[[16, 9], [16, 9]]}
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          <div styleName="no-results">Tuloksia ei löytynyt.</div>
        )}
        {viewMode === "episodes" && visibleCount < orderedStories.length && (
          <button styleName="loadmore-btn" onClick={() => setVisibleCount(prev => prev + 20)}>
            Näytä lisää
          </button>
        )}
      </div>
    </React.Fragment>
  );
};

SearchPage.propTypes = {
  data: PropTypes.object,
};

export { SearchPage };
